import React, { useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useFeatureFlagEnabled } from 'posthog-js/react';

import { SmileIcon } from '../components/icons/smile';
import { QuestionsCircleIcon, Button, Heading } from '../shared/ui';
import { DpdgCircleIcon } from '../components/icons/dpdg-circle';
import { SquaresIcon } from '../components/icons/squares';
import { PieIcon } from '../components/icons/pie';
import FlowerIcon from '../components/icons/flower';
import { AiSupervisorIcon } from '../components/icons/ai-supervisor';
import { ColorCodeIcon } from '../components/icons/color-сode';
import { CloudsIcon } from '../components/icons/clouds';
import { GrowAiIcon } from '../components/icons/grow-ai';
import { SandTherapyIcon } from '../components/icons/sand-therapy';

import ToolsCard from '../components/tools-card';

import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { selectUser, setUserData } from '../store/auth.slice';
import { useChangeUserMutation } from '../services/users.api';
import {
  FEATURE_FLAG_TOOL_COLOR_CODE,
  FEATURE_FLAG_TOOL_CLOUDS,
  FEATURE_FLAG_TOOL_SAND_THERAPY,
} from '../constants/featureFlags';

const Tools = () => {
  const psyTools = useAppSelector((state) => state.psyToolsSlice.tools);
  const params = useLocation();
  const userData = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const [changeUser] = useChangeUserMutation();
  const isColorCodeEnabled = useFeatureFlagEnabled(FEATURE_FLAG_TOOL_COLOR_CODE);
  const isCloudsEnabled = useFeatureFlagEnabled(FEATURE_FLAG_TOOL_CLOUDS);
  const isSandTherapyEnabled = useFeatureFlagEnabled(FEATURE_FLAG_TOOL_SAND_THERAPY);

  const visibleToolDescription = useMemo(() => {
    const baseTools = ['«Детектор эмоций»', '«Подкинь вопрос»', '«ДПДГ онлайн»', '«Колесо баланса»'];

    return [
      ...baseTools,
      '«Ai GROW»',
      isColorCodeEnabled ? '«Цветовой код»' : null,
      isCloudsEnabled ? '«Облака»' : null,
      isSandTherapyEnabled ? '«Песочная терапия»' : null,
    ]
      .filter(Boolean)
      .join(', ');
  }, [isColorCodeEnabled, isCloudsEnabled, isSandTherapyEnabled]);
  const [voteArr, setvoteArr] = useState<string[]>([]);

  useEffect(() => {
    if (!userData) return;
    if (userData.voted_psy_tools.length > 0 && voteArr.length === 0) {
      setvoteArr(userData.voted_psy_tools);
    }
  }, [userData, voteArr]);

  const handleSave = async () => {
    try {
      if (!userData) return;

      dispatch(setUserData({ ...userData, voted_psy_tools: voteArr }));
      await changeUser({
        id: userData.id,
        voted_psy_tools: voteArr,
      });

      toast.success('Данные успешно сохранены');
    } catch (error) {
      toast.error('Произошла ошибка');
    }
  };

  return (
    <div className="grow">
      {params.pathname.split('/').length === 2 && (
        <>
          <Heading size="xl" className="mb-5">
            Инструменты видеокабинета ZiNGiZi
          </Heading>
          <p className="text-ui text-ink-muted mb-5">
            Видеокабинет ZiNGiZi содержит колоды метафорических карт; инструменты {visibleToolDescription} и набор ИИ
            ассистентов для ведения журнала.
          </p>

          <div className="flex flex-wrap gap-[49px_26px] mb-5">
            <Link to={'emotion-detector'} className="w-[150px]">
              <div className="w-full h-[180px] flex items-center justify-center bg-accent mb-[15px] rounded-control">
                <SmileIcon fill="#C00000" opacity="1" />
              </div>
              <p className="text-ui-s text-center text-ink-muted">Детектор эмоций</p>
            </Link>
            <Link to={'metaphorical-cards'} className="w-[150px]">
              <div className="w-full h-[180px] flex items-center justify-center bg-accent mb-[15px] rounded-control">
                <FlowerIcon fill="#C00000" />
              </div>
              <p className="text-ui-s text-center text-ink-muted">Метафорические карты</p>
            </Link>
            <Link to={'question'} className="w-[150px]">
              <div className="w-full h-[180px] flex items-center justify-center bg-accent mb-[15px] rounded-control">
                <QuestionsCircleIcon fill="#C00000" width={52} height={52} needHover={false} />
              </div>
              <p className="text-ui-s text-center text-ink-muted">Подкинь вопрос</p>
            </Link>
            <Link to={'dpdg'} className="w-[150px]">
              <div className="w-full h-[180px] flex items-center justify-center bg-accent mb-[15px] rounded-control">
                <DpdgCircleIcon fill="#C00000" opacity="1" fillBg="#DEC05E" />
              </div>
              <p className="text-ui-s text-center text-ink-muted">ДПДГ онлайн</p>
            </Link>
            <Link to={'life-balance-wheel'} className="w-[150px]">
              <div className="w-full h-[180px] flex items-center justify-center bg-accent mb-[15px] rounded-control">
                <PieIcon fill="#C00000" classNameRect="translate(5, 5)scale(2.7)" />
              </div>
              <p className="text-ui-s text-center text-ink-muted">Колесо баланса</p>
            </Link>
            <Link to={'ai-assistants'} className="w-[150px]">
              <div className="w-full h-[180px] flex items-center justify-center bg-accent mb-[15px] rounded-control relative">
                <AiSupervisorIcon width={64} height={64} />
              </div>
              <p className="text-ui-s text-center text-ink-muted">ИИ Ассистенты</p>
            </Link>
            <Link to={'ai-grow'} className="w-[150px]">
              <div className="w-full h-[180px] flex items-center justify-center bg-accent mb-[15px] rounded-control relative">
                <div className="absolute top-2 right-2 px-2 py-[2px] rounded-full bg-white text-micro font-medium text-primary uppercase tracking-[0.08em]">
                  Новинка
                </div>
                <GrowAiIcon width={64} height={64} fill="#C00000" fillBg="#DEC05E" />
              </div>
              <p className="text-ui-s text-center text-ink-muted">Ai GROW</p>
            </Link>
            {isColorCodeEnabled && (
              <Link to={'color-code'} className="w-[150px]">
                <div className="w-full h-[180px] flex items-center justify-center bg-accent mb-[15px] rounded-control">
                  <ColorCodeIcon width={64} height={64} fill="#C00000" fillBg="#DEC05E" />
                </div>
                <p className="text-ui-s text-center text-ink-muted">Цветовой код</p>
              </Link>
            )}
            {isCloudsEnabled && (
              <Link to={'clouds'} className="w-[150px]">
                <div className="w-full h-[180px] flex items-center justify-center bg-accent mb-[15px] rounded-control">
                  <CloudsIcon width={64} height={64} fill="#C00000" fillBg="#DEC05E" />
                </div>
                <p className="text-ui-s text-center text-ink-muted">Облака</p>
              </Link>
            )}
            {isSandTherapyEnabled && (
              <Link to={'sand-therapy'} className="w-[150px]">
                <div className="w-full h-[180px] flex items-center justify-center bg-accent mb-[15px] rounded-control relative">
                  <div className="absolute top-2 right-2 px-2 py-[2px] rounded-full bg-white text-micro font-medium text-primary uppercase tracking-[0.08em]">
                    Новинка
                  </div>
                  <SandTherapyIcon width={64} height={64} fill="#C00000" fillBg="#DEC05E" />
                </div>
                <p className="text-ui-s text-center text-ink-muted">Песочная терапия</p>
              </Link>
            )}
          </div>

          <p className="text-ui text-ink-muted mb-[10px]">Библиотека инструментов постоянно пополняется.</p>
          <div className="flex flex-wrap gap-[49px_26px] mb-5">
            <div className="w-[150px]">
              <div className="w-full h-[180px] flex items-center justify-center bg-ink-muted mb-[15px] rounded-control relative">
                <div className="w-full absolute top-0 left-0 p-[5px] bg-accent text-ui-xs-strong text-ink rounded-t-control text-center">
                  Скоро релиз
                </div>
                <SquaresIcon needHover={false} />
              </div>
              <p className="text-ui-s text-center text-ink-muted">
                Техника
                <br /> холотропного дыхания
              </p>
            </div>
            <div className="w-[150px]">
              <div className="w-full h-[180px] flex items-center justify-center bg-ink-muted mb-[15px] rounded-control relative">
                <div className="w-full absolute top-0 left-0 p-[5px] bg-accent text-ui-xs-strong text-ink rounded-t-control text-center">
                  Скоро релиз
                </div>
                <SquaresIcon needHover={false} />
              </div>
              <p className="text-ui-s text-center text-ink-muted">Техника мышечной релаксации</p>
            </div>
            <div className="w-[150px]">
              <div className="w-full h-[180px] flex items-center justify-center bg-ink-muted mb-[15px] rounded-control relative">
                <div className="w-full absolute top-0 left-0 p-[5px] bg-accent text-ui-xs-strong text-ink rounded-t-control text-center">
                  Скоро релиз
                </div>
                <SquaresIcon needHover={false} />
              </div>
              <p className="text-ui-s text-center text-ink-muted">
                Техника
                <br /> модуляции установок
              </p>
            </div>
          </div>

          <p className="text-ui-strong text-primary mb-[10px]">
            Проголосуйте за инструменты, которые вы хотите видеть на сервисе ZiNGiZi
          </p>
          <p className="text-ui text-ink-muted mb-[10px]">
            Пожалуйста, проголосуйте и помогите определить, какие инструменты для вас наиболее важны. Отметьте все те,
            которые вы ждете с нетерпением и хотели бы использовать в своей практике.
          </p>
          <div className="flex flex-wrap gap-[49px_26px] pb-[20px]">
            {psyTools
              .filter((el) => el.status === 'in_plan')
              .map((item, idx) => (
                <div key={idx} className="w-[150px]">
                  <ToolsCard
                    id={item.id}
                    selected={voteArr}
                    setSelected={setvoteArr}
                    text={item.description}
                    cardName={item.label}
                    isBlocked={userData?.voted_psy_tools ? userData?.voted_psy_tools?.length > 0 : false}
                  />
                </div>
              ))}
          </div>
          <Button
            onClick={() => {
              if (voteArr.length === 0) return;
              handleSave();
            }}
            variant="primary"
            disabled={userData?.voted_psy_tools && userData?.voted_psy_tools?.length > 0 ? true : voteArr.length === 0}
            size="md"
            fullWidth
            className="mb-20"
          >
            Проголосовать
          </Button>
        </>
      )}

      <div>
        <Outlet />
      </div>
    </div>
  );
};

export default Tools;
